import { forwardRef, type InputHTMLAttributes } from 'react';
import { useFieldControl, useFieldInvalid } from './Field';
import Icon from './Icon';
import styles from './Input.module.css';
import type { IconName } from './icons';

export type InputSize = 'sm' | 'md' | 'lg';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputSize?: InputSize;
  /** A glyph inside the left edge. Decorative: it repeats what the label
      already says, so it stays hidden from assistive tech. */
  icon?: IconName;
}

/**
 * A single-line text input.
 *
 * Inside a `Field` it picks up its id, its description and its invalid state
 * from context, so the label, the help text and the error are wired together
 * without the call site naming any of them.
 *
 * Forwards its ref to the `<input>` itself, not to the icon wrapper, so callers
 * that need to focus or select the field get the element that can do it.
 */
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { inputSize = 'md', icon, className, ...rest },
  ref,
) {
  const field = useFieldControl();
  const invalid = useFieldInvalid();

  const input = (
    <input
      ref={ref}
      className={[
        styles.control,
        styles[inputSize],
        icon ? styles.withIcon : '',
        invalid ? styles.invalid : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...field}
      {...rest}
    />
  );

  if (!icon) return input;

  return (
    <span className={styles.iconWrap}>
      <Icon name={icon} size={15} className={styles.leadingIcon} />
      {input}
    </span>
  );
});

export default TextInput;
